/**
 * Outcomes Monitoring Report — constants
 *
 * Colours are JS constants (the house convention for outcome/severity palettes,
 * see cause-report/constants). Band + detriment hexes are the design-system
 * tokens confirmed in the prototype:
 *   bands    — low #2D7B5C, moderate #C99D2C, high #9D0B4D
 *   detriment— dissatisfaction (amber) #F59E0B, strain (purple) #580095,
 *              harm (bittersweet) #FF7461
 */

import type { Band, DetrimentType, Framework } from '../types';

/** Reuse the shared cause-report cache window (60s). */
export { CAUSE_REPORT_STALE_TIME as MONITORING_STALE_TIME } from '../../cause-report/constants';

// --- Bands -------------------------------------------------------------------

/**
 * Band thresholds — cuts at 3.6 and 6.6 so the ranges are gapless (no dead space
 * at 3.51–3.59): low < 3.6, moderate 3.6–6.59, high >= 6.6. Must match the
 * backend severity cuts (BAND_LOW_MAX / BAND_HIGH_MIN).
 */
export function bandOf(score: number): Band {
  return score < 3.6 ? 'low' : score < 6.6 ? 'mod' : 'high';
}

export const BAND_COLORS: Record<Band, string> = {
  low: '#2D7B5C',
  mod: '#C99D2C',
  high: '#9D0B4D',
};

export const BAND_LABELS: Record<Band, string> = {
  low: 'Low',
  mod: 'Moderate',
  high: 'High',
};

/** Score range captions for the records-by-band tiles (gapless at 3.6 / 6.6). */
export const BAND_RANGE: Record<Band, string> = {
  low: 'conduct score 1 – 3.59',
  mod: 'conduct score 3.6 – 6.59',
  high: 'conduct score 6.6 – 10',
};

// --- Detriment ---------------------------------------------------------------

export interface DetrimentMeta {
  type: DetrimentType;
  label: string;
  short: string;
  /** Column descriptor (severity order): EXPRESSION / IMPACT / DETRIMENT. */
  tag: string;
  color: string;
}

/** Severity order: dissatisfaction, customer strain, foreseeable harm. */
export const DETRIMENT_META: DetrimentMeta[] = [
  { type: 'dissatisfaction', label: 'Dissatisfaction', short: 'Diss', tag: 'EXPRESSION', color: '#F59E0B' },
  { type: 'strain', label: 'Customer strain', short: 'Strain', tag: 'IMPACT', color: '#580095' },
  { type: 'harm', label: 'Foreseeable harm', short: 'Harm', tag: 'DETRIMENT', color: '#FF7461' },
];

export const GO_GOOD_VIBES = '#4342F5';

// --- Framework framing -------------------------------------------------------

export interface FrameworkConfig {
  eyebrow: string;
  title: string;
  standfirst: string;
  jurisdiction: string;
  outcomesHeading: string;
  sectionInsight: string;
}

export const FRAMEWORK_CONFIG: Record<Framework, FrameworkConfig> = {
  cd: {
    eyebrow: 'Outcomes monitoring report',
    title: 'Consumer Duty Outcomes',
    standfirst:
      'Conduct score and record volume by outcome, and the detriment mix behind the score — for board and regulatory monitoring.',
    jurisdiction: 'FCA · United Kingdom',
    outcomesHeading: 'Performance by outcome',
    sectionInsight:
      'Conduct score and volume are what you monitor; the detriment mix is how the score is arrived at. Open any outcome to break it down over time and reach the records behind the number.',
  },
  tcf: {
    eyebrow: 'Outcomes monitoring report',
    title: 'Treating Customers Fairly Outcomes',
    standfirst:
      'Conduct score and record volume by TCF outcome, and the detriment mix behind the score — for board and regulatory monitoring.',
    jurisdiction: 'FSCA · South Africa',
    outcomesHeading: 'Performance by outcome',
    sectionInsight:
      'Conduct score and volume are what you monitor; the detriment mix is how the score is arrived at. Open any outcome to break it down over time and reach the records behind the number.',
  },
};

// --- React Query keys --------------------------------------------------------

export const monitoringKeys = {
  all: ['outcomes-monitoring'] as const,
  overview: (datasetId: string, framework: Framework, filters: Record<string, unknown>) =>
    [...monitoringKeys.all, 'overview', datasetId, framework, filters] as const,
  detail: (
    datasetId: string,
    framework: Framework,
    outcomeId: string,
    grain: string,
    filters: Record<string, unknown>,
  ) => [...monitoringKeys.all, 'detail', datasetId, framework, outcomeId, grain, filters] as const,
};
